import { useEffect, useState } from 'react';

/**
 * A `Date` that re-renders the caller on a fixed tick — the clock, as a value.
 *
 * Why this exists (PROD-2444): "hide the windows that have passed" is not a
 * one-shot computation. A Book-a-device rail left open across the top of the
 * hour keeps offering the window that went while the operator was searching
 * for a patient, because `new Date()` inside a `useMemo` is frozen at the
 * dependency that last changed. Reading the clock through state is what makes
 * the list keep telling the truth.
 *
 * Resolution, stated rather than assumed: the tick is a plain interval from
 * mount, NOT aligned to the wall-clock minute, so a start can survive up to
 * `intervalMs` past its time. That is deliberate — alignment would buy nothing
 * here, because the server is the authority on whether a window is still free
 * and the rail already handles its refusal. What this must prevent is a strip
 * of chips that is visibly hours out of date, not a one-minute race.
 *
 * `intervalMs` is a dependency, so a caller may change cadence; it should be a
 * constant in practice. Three calendar components (`gutter-clock`,
 * `current-time-line`, `current-time-indicator-line`) each inline this same
 * ticker and predate it — folding them in is a separate change, not this one.
 */
export function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
